import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import * as React from "react";
import { Button, FormGroup, Input, Label, Modal, Table } from "../components/Modal";
import * as UserStore from "./UserStore";

interface UserActionModalsProps {
    user: any;
    organizationId: string;
    resetPasswordOpen: boolean;
    messageOpen: boolean;
    loginLinkOpen: boolean;
    claimsOpen: boolean;
    onCloseResetPassword: () => void;
    onCloseMessage: () => void;
    onCloseLoginLink: () => void;
    onCloseClaims: () => void;
}

export const UserActionModals = ({ user, organizationId, resetPasswordOpen, messageOpen, loginLinkOpen, claimsOpen, onCloseResetPassword, onCloseMessage, onCloseLoginLink, onCloseClaims }: UserActionModalsProps) => {
    const [password, setPassword] = React.useState("");
    const [passwordErrors, setPasswordErrors] = React.useState([] as any[]);
    const [message, setMessage] = React.useState("");
    const [link, setLink] = React.useState(window.location.origin + "/");
    const [minutes, setMinutes] = React.useState(15);
    const [loginLink, setLoginLink] = React.useState("");
    const [claims, setClaims] = React.useState([] as any[]);
    const [error, setError] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    React.useEffect(() => {
        setPassword("");
        setPasswordErrors([]);
        setMessage("");
        setLoginLink("");
        setError("");
    }, [user?.id]);

    React.useEffect(() => {
        setError("");
        setIsSubmitting(false);
    }, [resetPasswordOpen, messageOpen, loginLinkOpen, claimsOpen]);

    React.useEffect(() => {
        if (!claimsOpen || !user?.id) return;
        UserStore.GetUserClaims(user.id)
            .then((response) => setClaims(response.data))
            .catch(() => setError("Unable to load claims."));
    }, [claimsOpen, user?.id]);

    const resetPassword = () => {
        setIsSubmitting(true);
        UserStore.ResetPassword(user, password)
            .then((response) => {
                if (response.data.isError) setPasswordErrors(response.data.errors);
                else onCloseResetPassword();
            })
            .catch(() => setPasswordErrors([{ description: "Password reset failed unexpectedly." }]))
            .finally(() => setIsSubmitting(false));
    };

    const sendMessage = () => {
        setIsSubmitting(true);
        setError("");
        axios.post("/api/messaging/sendMessage", { to: [user.id], text: message })
            .then(onCloseMessage)
            .catch((response) => setError(response.response?.data?.message || "Unable to send this message."))
            .finally(() => setIsSubmitting(false));
    };

    const createLoginLink = () => {
        setIsSubmitting(true);
        setError("");
        axios.post("/Account/CreateLoginLinkForUser", { link, userID: user.id, organizationID: organizationId, minutes })
            .then((response) => {
                setLoginLink(response.data);
                navigator.clipboard?.writeText(response.data).catch(() => undefined);
            })
            .catch((response) => setError(response.response?.data?.message || "Unable to create a login link."))
            .finally(() => setIsSubmitting(false));
    };

    const changeUserRoleClaim = (remove: boolean) => {
        const claimType = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
        const request = remove ? UserStore.RemoveClaim(user.id, claimType, "User") : UserStore.AddClaim(user.id, claimType, "User");
        setIsSubmitting(true);
        request
            .then(() => UserStore.GetUserClaims(user.id))
            .then((response) => setClaims(response.data))
            .catch(() => setError("Unable to update claims."))
            .finally(() => setIsSubmitting(false));
    };

    return <>
        <Modal isOpen={resetPasswordOpen} onClose={onCloseResetPassword} title="Reset Password" icon={["fad", "key"]} iconColor="text-yellow-600">
            <div className="p-6 space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">Changing password for <b>{user?.firstName} {user?.lastName}</b></div>
                <FormGroup><Label>New password</Label><Input type="password" value={password} onChange={(event: any) => setPassword(event.target.value)} /></FormGroup>
                {passwordErrors.map((item, index) => <div key={index} className="text-sm text-red-700">{item.description}</div>)}
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3"><Button variant="secondary" onClick={onCloseResetPassword}>Cancel</Button><Button disabled={!password || isSubmitting} onClick={resetPassword}>Reset password</Button></div>
        </Modal>

        <Modal isOpen={messageOpen} onClose={onCloseMessage} title="Send Message" icon={["fad", "message"]} iconColor="text-green-600">
            <div className="p-6"><FormGroup><Label>Message to {user?.fullName || user?.email}</Label><Input type="textarea" rows={5} value={message} onChange={(event: any) => setMessage(event.target.value)} /></FormGroup>{error && <div className="text-sm text-red-700">{error}</div>}</div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex justify-end gap-3"><Button variant="secondary" onClick={onCloseMessage}>Cancel</Button><Button disabled={!message.trim() || isSubmitting} onClick={sendMessage}>Send</Button></div>
        </Modal>

        <Modal isOpen={loginLinkOpen} onClose={onCloseLoginLink} title="Create Login Link" icon={["fad", "link"]} iconColor="text-purple-600">
            <div className="p-6 space-y-4">
                <FormGroup><Label>Redirect URL</Label><Input type="url" value={link} onChange={(event: any) => setLink(event.target.value)} /></FormGroup>
                <FormGroup><Label>Valid for (minutes)</Label><Input type="number" min={1} value={minutes} onChange={(event: any) => setMinutes(Number(event.target.value))} /></FormGroup>
                <Button fullWidth onClick={createLoginLink} disabled={isSubmitting}>Create and copy link</Button>
                {loginLink && <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800 break-all"><FontAwesomeIcon icon={["fad", "check-circle"]} className="mr-2" />{loginLink}</div>}
                {error && <div className="text-sm text-red-700">{error}</div>}
            </div>
        </Modal>

        <Modal isOpen={claimsOpen} onClose={onCloseClaims} title="Manage Claims" icon={["fad", "shield-check"]} iconColor="text-blue-600" size="lg">
            <div className="p-6 space-y-4">
                <div className="overflow-x-auto"><Table><thead><tr><th className="text-left p-2">Type</th><th className="text-left p-2">Value</th></tr></thead><tbody>{claims.map((claim, index) => <tr key={`${claim.type}-${claim.value}-${index}`}><td className="p-2 text-xs break-all">{claim.type}</td><td className="p-2 text-sm">{claim.value}</td></tr>)}</tbody></Table></div>
                <div className="flex flex-wrap gap-2"><Button size="sm" onClick={() => changeUserRoleClaim(false)} disabled={isSubmitting}>Add User role</Button><Button size="sm" variant="outline-danger" onClick={() => changeUserRoleClaim(true)} disabled={isSubmitting}>Remove User role</Button></div>
                {error && <div className="text-sm text-red-700">{error}</div>}
            </div>
        </Modal>
    </>;
};
